// The justification rule of an AI handoff (CHT-1955). A pure function: no model, no clock, no I/O.
//
// THE FAULT. On staging the real model once called `requestHandoff` (`CUSTOMER_REQUESTED_PERSON`) on the benign prompt "Hello again. I
// am planning a short trip to Goa." The customer had not asked for a person at that moment. The history held person requests that nobody
// had answered: the AI is silent while a handoff waits or a person holds the conversation, and a release or a cancel does not put a word
// into the history. The model read those old requests as still open. The history cannot say what is open, so the tool asks the
// SERVER-SET facts of the turn.
//
// THE ERROR DIRECTION. This rule fails SAFE.
//   - A wrong "allow" costs one unnecessary handoff. That is the behaviour of before this rule.
//   - A wrong "refuse" turns away a customer who needs a person (an emergency, a payment or legal problem, a complaint). That is the
//     severe error, and it comes AFTER the model already decided that a person is needed.
// So the rule refuses only when the evidence is strong, and allows in every doubtful case, in every language and script.
//
// THE RULE.
//   1. Only the reason `CUSTOMER_REQUESTED_PERSON` is guarded. It is the reason of the staging fault ("the customer asked for a person"),
//      and the model can only mis-read it from old messages. Every other reason is a topic or a state of the run (a refund, a
//      complaint, an emergency, a supplier, a tool result). It passes unchanged.
//   2. Facts come from the SERVER, never from the model: the customer messages of the CURRENT WINDOW (see `handoffTurnFactsOf`: after the
//      last message of another author and after the end of the last handoff), the assistant message right before the window, and whether
//      a tool of this run asked for a person.
//   3. The handoff is refused (`justified: false`) only when ALL customer text of the window is PLAINLY BENIGN: greetings, thanks,
//      acknowledgements and plain travel-planning statements or questions, built only from a small closed vocabulary. Anything else is
//      "not plainly benign" and the handoff is allowed: a word that the vocabulary does not hold, a script that is not Latin, an emoji, a
//      typo of a person word ("humna"), a short message in another language, shouting, a long message, a window of many messages.
//   4. A person already offered or promised wins: when the assistant message right before the window mentions a person at all (an offer
//      "Would you like a concierge?", a promise "Let me get a person for you.", an announcement "A member of our team will reach out."),
//      any short customer answer without a negation in the window is justified.
//      Two more doubts: a bare "and?", "yes?", "so?" or "?" is never plain (a customer who waits writes such a message), and after a
//      CANCELLED handoff (or an end that could not be read) a window made only of greetings, with at least one ping in it, ("hello?", "are you there?",
//      "anyone?", "any update?", "still waiting", "how long") is not plain either: the supervisor's cancel told the customer nothing.
//   5. A window with no customer message, and a tool of this run that answered `REQUIRES_HUMAN` or the hint `HANDOFF`, are justified.
export const HANDOFF_JUSTIFICATION_BASES = Object.freeze([
    "REASON_NOT_GUARDED",
    "TOOL_REQUIRES_HUMAN",
    "NO_CUSTOMER_MESSAGE",
    "OFFER_ACCEPTED",
    "NOT_PLAINLY_BENIGN",
]);
export const HANDOFF_REFUSAL_CODE = "WINDOW_PLAINLY_BENIGN";
/** The only reason that the rule guards. */
export const HANDOFF_GUARDED_REASON = "CUSTOMER_REQUESTED_PERSON";
/** More customer messages than this in one window: the customer wrote a lot without an answer. The rule does not judge it. */
export const WINDOW_MAX_MESSAGES = 5;
/** A longer message can hold a request inside plain words. The rule does not judge it. */
export const MESSAGE_MAX_CHARS = 400;
/** Lower case, accents folded, apostrophes and punctuation turned into spaces, one space between words. */
export function normalizeForHandoffRule(text) {
    return text
        .normalize("NFKD")
        .replace(/\p{M}+/gu, "")
        .toLowerCase()
        .replace(/[‘’ʼ`']/g, " ")
        .replace(/[^\p{L}\p{N}]+/gu, " ")
        .trim()
        .replace(/\s+/g, " ");
}
// --- the plainly benign vocabulary (data) --------------------------------------------------------------------------------------
const words = (list) => list.split(/\s+/).filter((word) => word !== "");
/**
 * Whole answers of courtesy. A clause that is a run of these (with the fillers below) is plainly benign. Folded text, longest first.
 * Left out on purpose: a bare "no", a bare "please" ("please!" can be a plea) and "pls".
 */
const ACKNOWLEDGEMENTS = [
    "hello again",
    "hi again",
    "hey again",
    "hello there",
    "hi there",
    "hey there",
    "good morning",
    "good afternoon",
    "good evening",
    "good night",
    "thank you so much",
    "thank you very much",
    "thank you",
    "thanks so much",
    "thanks a lot",
    "many thanks",
    "thanks",
    "thx",
    "cheers",
    "hello",
    "hi",
    "hey",
    "okay",
    "ok",
    "great",
    "cool",
    "nice",
    "perfect",
    "awesome",
    "wonderful",
    "lovely",
    "good",
    "fine",
    "alright",
    "all right",
    "got it",
    "noted",
    "understood",
    "i see",
    "i understand",
    "makes sense",
    "sounds good",
    "sounds great",
    "sounds perfect",
    "that works",
    "that sounds good",
    "that sounds great",
    "that s great",
    "that s perfect",
    "that s helpful",
    "that helps",
    "that is helpful",
    "that is great",
    "that is perfect",
    "that is all for now",
    "that is all",
    "that s all for now",
    "that s all",
    "it is me again",
    "it s me again",
    "i am back",
    "i m back",
    "we are back",
    "are you there",
    "what did we talk about earlier",
    "what did we discuss earlier",
    "what did we discuss",
    "yes please",
    "yes",
    "yeah",
    "yep",
    "yup",
    "sure thing",
    "sure",
    "of course",
    "absolutely",
    "definitely",
    "no thanks",
    "no thank you",
    "not now",
    "maybe later",
    "never mind",
    "nevermind",
    "goodbye",
    "bye",
    "see you later",
    "see you",
    "have a nice day",
    "have a good day",
    // Fillers that follow a thanks.
    "for the help",
    "for your help",
    "for all the help",
    "for that",
    "for everything",
    "for the info",
    "for the information",
    "for the recommendations",
    "for the suggestions",
    "so much",
    "very much",
    "again",
    "then",
    "and",
].sort((a, b) => b.length - a.length);
/** Emoji of a plain good mood. Any other emoji or pictograph makes the message "not plainly benign". */
const FRIENDLY_EMOJI = /[👍😊🙂😀😄]/gu;
/** A clause of one bare word of assent or of waiting, with a question mark: "and?", "yes?", "so?". Never plain. */
const BARE_QUESTION = /(?:^|[.!?;,\n])\s*(?:and|yes|yeah|yep|yup|so|then|ok|okay|sure|well|right|no)\s*\?/iu;
/** "The villa?", "Our room?": a definite noun and a question mark, nothing else. A customer who waits or is confused. Never plain. */
const BARE_NOUN_QUESTION = /(?:^|[.!?;,\n])\s*(?:the|our|my|this|that|his|her|your)\s+\p{L}+\s*\?/iu;
/**
 * Greetings and pings: what a customer writes when nobody has answered. Only for a window that is already plainly benign, after a CANCELLED
 * handoff (see `HandoffEndKind`). Folded text, longest first. Every phrase is a plain phrase or a plain word of the vocabulary.
 */
const GREETINGS_AND_PINGS = [
    "hello again",
    "hi again",
    "hey again",
    "hello there",
    "hi there",
    "hey there",
    "good morning",
    "good afternoon",
    "good evening",
    "are you still there",
    "are you there",
    "is anyone there",
    "is anybody there",
    "anyone there",
    "anybody there",
    "you there",
    "any updates",
    "any update",
    "any news",
    "still waiting",
    "how much longer",
    "how long",
    "it is me again",
    "it s me again",
    "i am back",
    "i m back",
    "we are back",
    "anyone",
    "anybody",
    "hello",
    "hi",
    "hey",
    "and",
    "again",
    "there",
].sort((a, b) => b.length - a.length);
// Left OUT of the function words on purpose (CHT-1955 PR review, G1): "out", "off", "up", "down", "here", "where", "somewhere" and "anywhere".
// "Get me out of here", "Where is our villa" and "Find us somewhere tonight" are built from them: a customer who is stranded or lost. The
// verbs "get", "go", "going", "find", "stay" and "staying" are function words, not content words: a clause of such verbs and pronouns alone
// ("Get us in", "What is going on?") holds nothing to plan, so it is never plain.
// Left OUT of the function words on purpose (second review, F3): "now", "need" and "needs". "We need a place to stay tonight", "find us another
// villa now" and "we are at the hotel now" are a customer who is stranded, not travel planning. "tonight" and "today" stay, but only
// in a clause that holds no lodging word (see `isUrgentShape`): "dinner tonight" is planning, "a place to stay tonight" is not.
/** Words with no content of their own: they join the content words of a travel statement or question. */
const FUNCTION_WORDS = new Set(words("a an the and or but so if then than that this these those there it its i me my we us our you your he she they them their his her " +
    "of to in on at by for from with about into over under near around after before during between through as " +
    "is are was were be been being am do does did done have has had will would can could shall should may might " +
    "what which when how who some any all more most other another few many much each every both only just also still even again " +
    "today tonight tomorrow yesterday soon later first second third last next one two three four five six seven eight nine ten " +
    "something anything everything else rest better prefer prefers relaxed while continue earlier help " +
    "get go going find stay staying tell give show suggest recommend list compare look looking search see visit visiting explore discover try choose pick decide add " +
    "please thanks thank hello hi hey good great nice ok okay sure yes yeah yep cool perfect awesome lovely wonderful amazing fine alright " +
    "sounds looks works want wants like would wondering interested thinking hoping hope love " +
    "very really quite " +
    "m s d ll re ve " +
    "time times"));
/** Words that give a clause its content: the trip, the places, the plain things a concierge is asked about. */
const CONTENT_WORDS = new Set(words("trip trips travel travelling traveling holiday holidays vacation getaway weekend week weeks days day month summer winter spring autumn " +
    "monday tuesday wednesday thursday friday saturday sunday january february march april may june july august september october november december " +
    "morning evening afternoon night " +
    "villa villas hotel hotels resort resorts room rooms place places restaurant restaurants cafe cafes bar bars dinner lunch breakfast brunch " +
    "food cuisine menu menus table tables spa beach beaches pool view views garden city town island islands mountain mountains " +
    "hike hikes hiking surf surfing swim swimming snorkeling snorkelling sightseeing tour tours museum museums nightlife shopping weather " +
    "plan planning save saved " +
    "open opens opening hours " +
    "idea ideas option options recommendation recommendations suggestion suggestions information info details " +
    "quiet cozy cosy romantic family friendly vegetarian vegan cheap affordable luxury short long nearby popular best local scenic private " +
    "beautiful peaceful authentic famous cheaper nicer calm calmer boutique seafood sea night nights north south east west part old floor terrace rooftop " +
    "couple couples kids kid child children friends group people adults adult " +
    "goa paris lisbon london rome bali dubai tokyo kyoto madrid barcelona venice florence athens istanbul bangkok singapore sydney " +
    "mumbai delhi jaipur kerala udaipur manali shimla"));
/** Words that put a capitalised name into the sentence as a PLACE: "a trip to Kyoto", "dinner in Porto", "near Old Town". */
const LOCATION_PREPOSITIONS = new Set(words("to in at near around from outside across throughout towards into"));
const PLACE_MAX_TOKENS = 3;
/**
 * A second line of defence, on the folded clause. Not one word of the vocabulary above is in it (a test checks that), so it matters for
 * the one open slot: a capitalised place name. "a trip to Manager" must not pass as a place.
 */
const SIGNAL_WORD = /\b(?:human\w*|person\w*|someone|somebody|anyone|anybody|agent\w*|manager\w*|supervisor\w*|concierge\w*|staff|team|representative\w*|operator\w*|colleague\w*|specialist\w*|advis[eo]r\w*|consultant\w*|owner\w*|receptionist\w*|bot|robot|ai|call\w*|phone\w*|asap|immediate\w*|stranded|stuck|now|ring|contact\w*|speak\w*|talk\w*|chat\w*|connect\w*|transfer\w*|escalat\w*|forward\w*|email\w*|message\w*|reach|refund\w*|money|pay\w*|charg\w*|cancel\w*|complain\w*|police|doctor|hospital|ambulance|fire|emergency|urgent\w*|legal\w*|lawyer\w*|book\w*|reserv\w*|chang\w*|amend\w*|modif\w*|move|resched\w*|passport|lost|stolen|robbed|anywhere|somewhere|inside|outside|locked|lock\w*|trapped|sick|ill|hurt)\b/;
/** "help" is plain only inside "help me plan ...". Everything else with "help" is a possible call for help. */
const PLAIN_HELP = /\bhelp (?:me|us) (?:plan|find|choose|pick|decide|pack|organi[sz]e|put together|narrow|compare|explore|figure out)\b/;
const clausesOf = (raw) => raw
    .split(/[.!?;\n]+|,\s/u)
    .map((clause) => clause.trim())
    .filter((clause) => clause !== "");
const foldWord = (word) => word
    .normalize("NFKD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase();
const NUMBER_TOKEN = /^\d+(?:st|nd|rd|th|am|pm)?$/;
/** A clause that is only a run of phrases of the list (longest first). */
function isRunOf(phrases, folded) {
    let rest = folded;
    while (rest !== "") {
        const phrase = phrases.find((candidate) => rest === candidate || rest.startsWith(`${candidate} `));
        if (phrase === undefined)
            return false;
        rest = rest.slice(phrase.length).trim();
    }
    return true;
}
/** A clause that is only courtesy: a run of acknowledgement phrases. */
const isAcknowledgement = (folded) => isRunOf(ACKNOWLEDGEMENTS, folded);
const LODGING_WORD = /\b(?:stay|staying|room|rooms|hotel|hotels|villa|villas|resort|resorts|place|places|night|nights)\b/;
/** A clause about the present place of the customer ("we are at the hotel", "I am in Goa"): where someone is, not what to plan. */
const PRESENT_LOCATION = /\b(?:we|i) (?:are|am|m|re) (?:at|in|outside|inside|near)\b/;
/** "the trip is over", "our holiday is ruined": an ending, not a plan. */
const ENDED_STATE = /\b(?:is|are|was|were) (?:over|done|finished|ruined)\b/;
/** A time that is close: a need, when it comes with a verb of finding, going, getting or staying ("find us something for tonight"). */
const NEAR_TIME = /\b(?:tonight|today|now|tomorrow morning|this (?:morning|afternoon|evening)|right away)\b/;
const FIND_OR_GO_VERB = /\b(?:find|finding|go|going|get|getting|stay|staying|sleep|sleeping)\b/;
/** A first-person pronoun: the customer speaks of themself or of their own booking. */
const FIRST_PERSON = /\b(?:we|our|ours|my|mine|me|us|i)\b/;
/** A question that opens with a wh-word and holds a first-person pronoun ("Which hotel are we in?", "What do I do?"). "What about ..." is a suggestion. */
const WH_FIRST_PERSON = /^(?:(?:and|so|ok|okay|hello|hi|hey|well|but) )*(?:what|which|who|whom|when|how|why)\b(?! about)/;
/** "what is going on", "Is the villa open", "anything open tonight": a customer at a closed door or in the dark, not a customer who plans. */
const GOING_ON = /\bgoing on\b/;
const IS_OPEN = /\b(?:is|are|was|were) (?:\w+ ){0,2}open\b/;
/** "Do we have a room", "Can I get a table": the customer asks for something that they may not have. */
const DO_WE_HAVE = /\b(?:do|does|did|will|can|could) (?:we|i) (?:still )?(?:have|get|need)\b/;
/** "Find the villa", "Open the hotel", "Can you get us a room": a verb of finding or opening with the lodging of the customer or a room for them. */
const FIND_THE_LODGING = /\b(?:find|open|unlock|enter|reach|locate)\b (?:\w+ ){0,2}?the (?:villa|hotel|room|resort|place)\b/;
const GET_US_A_LODGING = /\b(?:find|get|book) (?:us|me) (?:a|an|another|any|some) (?:room|rooms|hotel|villa|place|bed)\b/;
const DEFINITE = "(?:the|our|my|this|that|these|those|their|his|her|your)";
/** The lodging of a customer, said with a definite word: "the villa", "our room", "the Goa hotel". Not "a villa" and not "the first two villas". */
const DEFINITE_LODGING = new RegExp(`\\b${DEFINITE} (?:\\w+ )?(?:villa|villas|hotel|hotels|room|rooms|resort|resorts|place|places|apartment|house|home|cabin|hostel|lodge|accommodation|stay)\\b`);
/** A verb of getting somewhere, finding something or staying: with the definite lodging it is a customer who is not where they want to be. */
const MOVE_VERB = /\b(?:get|getting|take|bring|let|find|finding|open|enter|reach|locate|unlock|go|going|leave|come|stay|staying|sleep|sleeping|looking for|trying|need|check in|into|out of|show (?:us|me))\b/;
/** A place that a customer can be shut out of, with a verb that opens it or takes the customer there. */
const DEFINITE_PLACE = new RegExp(`\\b${DEFINITE} (?:\\w+ )?(?:pool|restaurant|beach|spa|bar|cafe|museum|gate|door|airport|terminal|taxi|car)\\b`);
const OPEN_OR_TAKE_VERB = /(?:^|\b(?:please|you|someone|somebody|can|could|will) )open\b|\b(?:into|unlock|enter|let (?:us|me|them)|take (?:us|me)|bring (?:us|me)|get (?:us|me)|leave)\b/;
/** "Get us to ...", "Take me into ...", "Get us in": a request to be moved or let in. Planning has no such sentence. */
const MOVE_REQUEST = /\b(?:get|take|bring|find|show|let) (?:us|me) (?:to|into|in|out|back|home|there|here|near|inside|through)\b/;
const STAY_LONGER = /\b(?:stay|staying|extend|extending|sleep)\b.*\banother\b/;
/** "we are staying at the villa", "I am sleeping in a room": where the customer is right now. */
const STAYING_AT = /\b(?:we|i) (?:are|am|m|re) (?:staying|sleeping|living) (?:at|in|inside|outside)\b (?:the|a|an|our|my)\b/;
/** "our villa", "my room", "our stay": a booking of the customer, not a thing to plan. */
const OWN_LODGING = /\b(?:our|my) (?:\w+ )?(?:stay|room|rooms|hotel|hotels|villa|villas|resort|resorts|place|night|nights)\b/;
/**
 * Shapes of plain words that are not planning (second review, F3; PR review, G1): a lodging need for tonight or today, where the customer is
 * right now, a trip that ended, a verb of finding or going with a near time, the booking of the customer ("our villa?"), a wh-question about the
 * customer ("where are we staying"). A doubt is "allow", so each one is "not plain".
 */
function isUrgentShape(folded) {
    if (/\b(?:tonight|today)\b/.test(folded) && LODGING_WORD.test(folded))
        return true;
    if (NEAR_TIME.test(folded) && FIND_OR_GO_VERB.test(folded))
        return true;
    if (OWN_LODGING.test(folded))
        return true;
    if (DEFINITE_LODGING.test(folded) && MOVE_VERB.test(folded))
        return true;
    if (DEFINITE_PLACE.test(folded) && OPEN_OR_TAKE_VERB.test(folded))
        return true;
    if (MOVE_REQUEST.test(folded))
        return true;
    if (STAYING_AT.test(folded) || STAY_LONGER.test(folded))
        return true;
    if (GOING_ON.test(folded) || IS_OPEN.test(folded) || DO_WE_HAVE.test(folded))
        return true;
    if (FIND_THE_LODGING.test(folded) || GET_US_A_LODGING.test(folded))
        return true;
    if (/\bopen\b/.test(folded) && NEAR_TIME.test(folded))
        return true;
    if (WH_FIRST_PERSON.test(folded) && FIRST_PERSON.test(folded))
        return true;
    return PRESENT_LOCATION.test(folded) || ENDED_STATE.test(folded);
}
/** A clause of known words only, with at least one content word. A capitalised name after a place preposition counts as a known word. */
function isPlainStatement(rawClause, folded) {
    if (SIGNAL_WORD.test(folded) || isUrgentShape(folded))
        return false;
    if (/\bhelp\b/.test(folded) && !PLAIN_HELP.test(folded))
        return false;
    const tokens = rawClause.match(/[\p{L}\p{N}]+/gu) ?? [];
    if (tokens.length === 0)
        return false;
    let hasContent = false;
    let previous = "";
    let placeRun = 0;
    for (const token of tokens) {
        const word = foldWord(token);
        // "hours" is plain only as "opening hours" ("two hours" is a wait or a delay).
        if (word === "hours" && previous !== "opening" && previous !== "open")
            return false;
        if (CONTENT_WORDS.has(word)) {
            hasContent = true;
            placeRun = 0;
        }
        else if (FUNCTION_WORDS.has(word) || NUMBER_TOKEN.test(word)) {
            placeRun = 0;
        }
        else if (/^\p{Lu}\p{Ll}+$/u.test(token) &&
            (LOCATION_PREPOSITIONS.has(previous) || (placeRun > 0 && placeRun < PLACE_MAX_TOKENS))) {
            // A capitalised name right after a place preposition, or right after another part of such a name.
            hasContent = true;
            placeRun += 1;
        }
        else {
            return false;
        }
        previous = word;
    }
    return hasContent;
}
/** True when the message is plainly benign. Every doubt is `false`. */
function isPlainlyBenign(raw) {
    const text = raw.trim();
    if (text === "" || text.length > MESSAGE_MAX_CHARS)
        return false;
    // Another script (Devanagari, Arabic, CJK, Cyrillic, ...). Letters, digits, punctuation and symbols of Latin and Common are the rest.
    if (/[^\p{Script=Latin}\p{Script=Common}\p{Script=Inherited}]/u.test(text))
        return false;
    const withoutFriendlyEmoji = text.replace(FRIENDLY_EMOJI, " ");
    // Any other pictograph (an angry face, a raised hand, an SOS) is a feeling or a signal that the vocabulary cannot read.
    if (/\p{Extended_Pictographic}/u.test(withoutFriendlyEmoji))
        return false;
    // A bare "and?", "yes?", "so?" is a customer who waits for something. It is never plain, whatever came before.
    if (BARE_QUESTION.test(text) || BARE_NOUN_QUESTION.test(text))
        return false;
    // Shouting, and a run of exclamation marks, are distress until shown otherwise.
    if (/!{3,}/.test(text))
        return false;
    const letters = withoutFriendlyEmoji.replace(/[^\p{L}]/gu, "");
    if (letters.length >= 8 && letters === letters.toUpperCase())
        return false;
    // Only friendly emoji and punctuation: a thumbs-up is plain.
    if (letters === "")
        return (withoutFriendlyEmoji.replace(/[\p{P}\p{S}\s\d]/gu, "") === "" && withoutFriendlyEmoji !== text);
    const clauses = clausesOf(withoutFriendlyEmoji).map((clause) => ({
        clause,
        folded: normalizeForHandoffRule(clause),
    }));
    if (clauses.length === 0 || clauses.some(({ folded }) => folded === ""))
        return false;
    // A clause that is only "please" ("Compare the two villas, please.") belongs to the clause next to it. "please" alone is not plain.
    const spoken = clauses.filter(({ folded }) => folded !== "please");
    if (spoken.length === 0)
        return false;
    return spoken.every(({ clause, folded }) => isAcknowledgement(folded) || isPlainStatement(clause, folded));
}
/** A phrase that asks whether anybody is there or how long it takes. Folded text. */
const PING_PHRASE = /\b(?:are you still there|are you there|you there|anyone|anybody|any updates?|any news|still waiting|how much longer|how long)\b/;
/**
 * The message is only greetings and pings, and it is a PING: it holds a question mark or a ping phrase. A bare greeting ("Hi!", "Hello
 * again.") is small talk, not a customer who waits, so it is not a ping.
 */
function greetingPing(raw) {
    const clauses = clausesOf(raw.replace(FRIENDLY_EMOJI, " "))
        .map(normalizeForHandoffRule)
        .filter((folded) => folded !== "" && folded !== "please");
    if (clauses.length === 0 || !clauses.every((folded) => isRunOf(GREETINGS_AND_PINGS, folded))) {
        return null;
    }
    return raw.includes("?") || clauses.some((folded) => PING_PHRASE.test(folded))
        ? "PING"
        : "GREETING";
}
// --- the offer of a person -----------------------------------------------------------------------------------------------------
// A person word in the assistant message, in any form: an offer ("Would you like a concierge?"), a promise ("Let me get a person for you.",
// "A concierge will be with you shortly.") or an announcement ("A member of our team will reach out."). It is wide on purpose: a wider
// match only turns a refusal into an allow, which is the safe direction. Folded text.
const PERSON_MENTION = /\b(?:follow up|follow ups|concierge|concierges|team|teams|teammate|teammates|person|people|persons|someone|somebody|human|humans|colleague|colleagues|member|members|connect|connects|connected|connecting|take over|taking over|reach out|reaching out|get in touch|in touch|be with you|get back to you|hand over|hand you|hand this|pass this|pass it|pass you|speak|talk|call|contact|specialist|specialists|manager|managers|staff|representative|representatives|advisor|advisors|adviser|advisers|agent|agents|associate|expert|experts|personnel|persona|personas|personne|personnes|humano|humana|agente|agentes|humain|humaine|mensch|menschen|mitarbeiter|mitarbeiterin|kollege|kollegin|berater|beraterin|jemand|ansprechpartner|conserje|conseiller|conseillere|equipo|equipe|colega|collegue|collega|operatore|operador|atendente|asesor|asesora|alguien|qualcuno|squadra|pessoa|persoon|medewerker|mens|quelqu un|insaan|banda|aadmi|vyakti|team member)\b/;
/** The same, in scripts that are not Latin. On the raw text: the folding of `normalizeForHandoffRule` removes the marks of these scripts. */
const PERSON_MENTION_NON_LATIN = /人工|客服|同事|团队|團隊|スタッフ|担当|チーム|人間|사람|직원|상담|팀|человек|сотрудник|менеджер|команд|специалист|консультант|شخص|فريق|موظف|مدير|इंसान|व्यक्ति|टीम|कॉन्सिएज|सदस्य|साथी|אדם|צוות|נציג/u;
const mentionsPerson = (text) => PERSON_MENTION.test(normalizeForHandoffRule(text)) || PERSON_MENTION_NON_LATIN.test(text);
const NEGATION = /\b(?:no|not|nope|never|dont|don t|nahi|nahin|non|nein|nothing|neither|stop|cancel)\b/;
// Wide on purpose: a polite yes can be long ("yes please, that would be great, thank you so much for the help").
const ANSWER_MAX_WORDS = 16;
/** The assistant message ends with a question mark (a trailing quote, bracket or emoji does not count). */
const ENDS_WITH_QUESTION = /\?[\s"'”’)\]\p{Extended_Pictographic}]*$/u;
/**
 * An offer of help without a person word ("Should I escalate this?", "I can arrange a callback.", "I can pass your request on.", "Shall I flag
 * this for review?"). Folded text. Wide on purpose: it only turns a refusal into an allow.
 */
const OFFER_VERB = /\b(?:arrange|arranging|escalat\w*|pass(?:ing)? (?:on|this|it|that|your|along)|ask|asking|flag|flagging|review|call ?back|callback|help|helping|assist|forward\w*|check with|look into|looking into|loop in|follow up|sort (?:this|it|that) out|take care)\b/;
/** A whole answer of assent: "yes", "ok", "sure", "please do", "go for it". Thanks and farewells alone are not assent. */
const ASSENT_PHRASES = [
    "yes please",
    "please do",
    "go ahead",
    "go for it",
    "do that",
    "do it",
    "sounds good",
    "that sounds good",
    "that works",
    "that would be great",
    "that would be good",
    "that would be helpful",
    "that would be nice",
    "of course",
    "all right",
    "alright",
    "absolutely",
    "definitely",
    "okay",
    "ok",
    "yes",
    "yeah",
    "yep",
    "yup",
    "ya",
    "y",
    "sure",
    "please",
    "fine",
    "great",
    "perfect",
    "haan",
    "oui",
    "si",
    "ha",
    "thanks",
    "thank you",
    "so much",
    "very much",
    "then",
    "and",
].sort((a, b) => b.length - a.length);
/** At least one of the phrases that says "yes", so that "thanks" alone is not an assent. */
const ASSENT_CORE = /\b(?:yes|yeah|yep|yup|ya|y|ok|okay|sure|please|do|go|alright|all right|absolutely|definitely|fine|great|perfect|sounds good|that works|of course|haan|oui|si|ha)\b/;
const isAssent = (folded) => folded !== "" && ASSENT_CORE.test(folded) && isRunOf(ASSENT_PHRASES, folded);
/**
 * A short answer of assent follows an assistant message that ends with a question or that offers help (PR review, G2). The assistant may have
 * offered a callback or a review without a person word. A customer who says "yes" to it can be a customer who needs a person. Statements
 * and thanks are not assent here.
 */
function acceptsQuestionOrHelpOffer(window, assistantText) {
    if (assistantText === null)
        return false;
    if (!ENDS_WITH_QUESTION.test(assistantText.trim()) &&
        !OFFER_VERB.test(normalizeForHandoffRule(assistantText))) {
        return false;
    }
    return window.some((text) => {
        const folded = normalizeForHandoffRule(text);
        return (folded.split(" ").length <= ANSWER_MAX_WORDS &&
            !NEGATION.test(folded) &&
            isAssentAnswer(text, folded));
    });
}
/** Every clause of the answer is assent: "Yes please." "ok, great". */
function isAssentAnswer(raw, folded) {
    if (isAssent(folded))
        return true;
    const clauses = clausesOf(raw.replace(FRIENDLY_EMOJI, " "))
        .map(normalizeForHandoffRule)
        .filter((clause) => clause !== "");
    return clauses.length > 0 && clauses.every(isAssent);
}
/**
 * The last assistant message mentions a person (an offer, a promise or an announcement), and a short answer without a negation is in the
 * window. After "Let me get a person for you." a customer's "thanks" answers the promise: if the model asks for the person again (the first
 * call can have failed, or the run can have timed out), the handoff must start.
 */
function acceptsOffer(window, assistantText) {
    if (assistantText === null)
        return false;
    if (acceptsQuestionOrHelpOffer(window, assistantText))
        return true;
    if (!mentionsPerson(assistantText))
        return false;
    return window.some((text) => {
        const folded = normalizeForHandoffRule(text);
        if (folded === "")
            return true;
        return folded.split(" ").length <= ANSWER_MAX_WORDS && !NEGATION.test(folded);
    });
}
// --- a question after a cancel -------------------------------------------------------------------------------------------------
/** A clause that opens like a question: a wh-word or an auxiliary ("did you see", "what is going on"). Folded clause, after greetings. */
const QUESTION_OPENING = /^(?:(?:and|so|ok|okay|hello|hi|hey|well|but|again|there) )*(?:what|where|when|which|who|whom|whose|why|how|did|do|does|can|could|are|is|will|would|was|were|have|has|should)\b/;
/** A question about what the AI or the team did: never a plan. */
const ASKS_ABOUT_THE_PAST = /\b(?:did you|have you|has anyone|were you|where did you|what happened)\b/;
/** Nouns of a trip: a question with one of them, and no first-person pronoun, is a planning question. The verbs of the vocabulary are not enough. */
const PLAN_ACTION_WORDS = new Set(words("plan planning save saved open opens opening hours idea ideas option options information info details"));
/** "our booking", "my villa", "our plan": the customer asks about what is theirs. With a question it is the state of the booking, not a plan. */
const OWN_BOOKING_REFERENCE = /\b(?:our|my) (?:\w+ )?(?:booking|bookings|reservation|reservations|request|requests|message|messages|stay|trip|villa|hotel|room|itinerary|plan|order|payment)\b/;
/** The sentences of a message that ask something: they end with a question mark or open like a question. Raw text, one string per sentence. */
function questionSentences(raw) {
    return raw
        .replace(FRIENDLY_EMOJI, " ")
        .split(/(?<=[.!?;\n])/u)
        .map((sentence) => sentence.trim())
        .filter((sentence) => sentence !== "")
        .filter((sentence) => {
        if (sentence.endsWith("?"))
            return true;
        const folded = normalizeForHandoffRule(sentence);
        return (clausesOf(sentence).some((clause) => QUESTION_OPENING.test(normalizeForHandoffRule(clause))) || QUESTION_OPENING.test(folded));
    });
}
/**
 * After a CANCELLED handoff (or an end that could not be read) the customer was told nothing. A question in the window ("Where did you go?",
 * "Did you find anything?", "What did we discuss earlier?") can be a customer who waits. It is NOT plain, unless it is a planning question
 * (PR review, G3): a noun of a trip in it, no question about the booking of the customer ("our villa", "my request") and no question about
 * what the AI did ("did you", "have you"). One waiting question in a message is enough.
 */
function isWaitingQuestion(raw) {
    return questionSentences(raw).some((sentence) => {
        const folded = normalizeForHandoffRule(sentence);
        if (ASKS_ABOUT_THE_PAST.test(folded) || OWN_BOOKING_REFERENCE.test(folded))
            return true;
        const hasTravelNoun = (sentence.match(/[\p{L}\p{N}]+/gu) ?? []).some((token, index, all) => {
            const word = foldWord(token);
            if (CONTENT_WORDS.has(word) && !PLAN_ACTION_WORDS.has(word))
                return true;
            return (/^\p{Lu}\p{Ll}+$/u.test(token) &&
                index > 0 &&
                LOCATION_PREPOSITIONS.has(foldWord(all[index - 1] ?? "")));
        });
        return !hasTravelNoun;
    });
}
// --- the rule ---------------------------------------------------------------------------------------------------------------------
export function assessHandoffJustification(input) {
    if (input.reasonCode !== HANDOFF_GUARDED_REASON) {
        return { justified: true, basis: "REASON_NOT_GUARDED" };
    }
    if (input.toolAskedForPerson)
        return { justified: true, basis: "TOOL_REQUIRES_HUMAN" };
    const window = input.windowCustomerTexts;
    if (window.length === 0 || window.every((text) => text.trim() === "")) {
        return { justified: true, basis: "NO_CUSTOMER_MESSAGE" };
    }
    if (acceptsOffer(window, input.previousAssistantText)) {
        return { justified: true, basis: "OFFER_ACCEPTED" };
    }
    if (window.length <= WINDOW_MAX_MESSAGES && window.every(isPlainlyBenign)) {
        // The supervisor cancelled a WAITING handoff and nobody told the customer: a window of only greetings and pings (a bare "Hi!" or "Hello
        // again." too, owner decision of 2026-10-17) is a customer who still waits more likely than small talk. A greeting with any trip content
        // ("Hello again. I am planning a short trip to Goa.", the staging fault) is not only a greeting and stays refused. The same window after
        // a release (a person spoke to the customer) stays refused. Only while the customer is still unanswered: once an AI answer (or any message
        // of another author) came after a CANCELLED handoff, a bare greeting or a ping is ordinary conversation again. An end that cannot be read
        // (`UNKNOWN`) allows whatever the answers: a doubt allows.
        const end = input.lastHandoffEnd ?? "NONE";
        const stillUnanswered = end === "UNKNOWN" || input.answeredSinceLastEnd !== true;
        if ((end === "CANCELLED" || end === "UNKNOWN") && stillUnanswered) {
            if (window.every((text) => greetingPing(text) !== null)) {
                return { justified: true, basis: "NOT_PLAINLY_BENIGN" };
            }
            if (window.some(isWaitingQuestion))
                return { justified: true, basis: "NOT_PLAINLY_BENIGN" };
        }
        return { justified: false, code: HANDOFF_REFUSAL_CODE, windowMessageCount: window.length };
    }
    return { justified: true, basis: "NOT_PLAINLY_BENIGN" };
}
/** The vocabulary, for the test that no word of it is a signal word and for the documentation. Not for the rule's callers. */
export const HANDOFF_BENIGN_VOCABULARY = Object.freeze({
    acknowledgements: ACKNOWLEDGEMENTS,
    functionWords: [...FUNCTION_WORDS],
    contentWords: [...CONTENT_WORDS],
    signalWord: SIGNAL_WORD,
});
